import { useState } from "react";
import { useStore, CAT_META } from "../store/Store";

// ─── QUICK VIEW POPUP ─────────────────────────────────────────────────────────
function QuickView({ p, onClose, onNav }) {
  const { dispatch, wish, toggleWish, notify } = useStore();
  const meta = CAT_META[p.category]||{color:"#EC4899",light:"#FDF2F8",emoji:"👗"};
  const [sc, setSc] = useState(p.colors?.[0]||"Default");
  const [ss, setSs] = useState(p.sizes?.[0]||"Free");
  const disc = Math.round((1-p.price/p.mrp)*100);

  const addCart = () => {
    dispatch({type:"ADD", p:{...p,sc,ss}});
    notify(p.name+" added!");
    onClose();
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20, backdropFilter:"blur(4px)" }}
      onClick={onClose}>
      <div style={{ background:"white", borderRadius:24, maxWidth:560, width:"100%", overflow:"hidden", boxShadow:"0 24px 80px rgba(0,0,0,0.3)", animation:"slideUp 0.3s ease" }}
        onClick={e=>e.stopPropagation()}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr" }}>
          {/* Image */}
          <div style={{ background:p.img?"white":meta.light, height:280, display:"flex", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden" }}>
            {p.img
              ? <img src={p.img} alt={p.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} loading="lazy"/>
              : <div style={{ fontSize:64, opacity:0.2 }}>{meta.emoji}</div>
            }
            {disc>0 && <span style={{ position:"absolute", top:12, left:12, background:"#EF4444", color:"white", fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:20 }}>{disc}% OFF</span>}
            <button onClick={()=>toggleWish(p.id)} style={{ position:"absolute", top:12, right:12, background:"white", border:"none", borderRadius:"50%", width:32, height:32, fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.1)" }}>
              {wish.includes(p.id)?"❤️":"🤍"}
            </button>
          </div>

          {/* Details */}
          <div style={{ padding:20 }}>
            <div style={{ fontSize:9, color:meta.color, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>{p.category}</div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:900, color:"#1a1a2e", marginBottom:8, lineHeight:1.3 }}>{p.name}</div>
            <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:12 }}>
              <span style={{ fontSize:20, fontWeight:900, color:"#1a1a2e" }}>₹{p.price?.toLocaleString("en-IN")}</span>
              {p.mrp>p.price && <span style={{ fontSize:12, color:"#aaa", textDecoration:"line-through" }}>₹{p.mrp?.toLocaleString("en-IN")}</span>}
            </div>

            {/* Color */}
            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:9, fontWeight:700, color:"#999", marginBottom:6, textTransform:"uppercase", letterSpacing:1 }}>Color: <span style={{ color:"#1a1a2e" }}>{sc}</span></div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {(p.colors||["Default"]).map(c=>(
                  <button key={c} onClick={()=>setSc(c)} style={{ padding:"4px 10px", fontSize:10, border:`2px solid ${sc===c?meta.color:"#e5e5e5"}`, background:sc===c?meta.light:"white", color:sc===c?meta.color:"#666", borderRadius:50, cursor:"pointer", fontWeight:sc===c?700:400 }}>{c}</button>
                ))}
              </div>
            </div>

            {/* Size */}
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:9, fontWeight:700, color:"#999", marginBottom:6, textTransform:"uppercase", letterSpacing:1 }}>Size: <span style={{ color:"#1a1a2e" }}>{ss}</span></div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {(p.sizes||["Free"]).map(sz=>(
                  <button key={sz} onClick={()=>setSs(sz)} style={{ minWidth:36, padding:"5px 10px", fontSize:11, border:`2px solid ${ss===sz?meta.color:"#e5e5e5"}`, background:ss===sz?"#1a1a2e":"white", color:ss===sz?"white":"#666", borderRadius:8, cursor:"pointer", fontWeight:700 }}>{sz}</button>
                ))}
              </div>
            </div>

            <button onClick={addCart} disabled={p.stock===0} style={{ width:"100%", padding:"10px", background:meta.color, color:"white", border:"none", borderRadius:50, fontSize:12, fontWeight:800, cursor:"pointer", marginBottom:8, opacity:p.stock===0?0.5:1 }}>
              🛒 Add to Cart
            </button>
            <button onClick={()=>{onClose();onNav("product",p.id);}} style={{ width:"100%", padding:"10px", background:"white", color:"#1a1a2e", border:"1px solid #e5e5e5", borderRadius:50, fontSize:12, fontWeight:600, cursor:"pointer" }}>
              View Full Details →
            </button>
          </div>
        </div>
      </div>
      {/* Close button */}
      <button onClick={onClose} style={{ position:"fixed", top:20, right:20, background:"white", border:"none", borderRadius:"50%", width:40, height:40, fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 16px rgba(0,0,0,0.2)" }}>✕</button>
    </div>
  );
}

// ─── PRODUCT CARD ─────────────────────────────────────────────────────────────
// Signature look: a colored "fabric tag" ribbon folds over the top-left corner
// (echoes a garment care-label), and a thin category-colored bar runs along
// the bottom of the photo — small details that read as boutique, not generic.
export default function ProductCard({ p, onNav, compact }) {
  const { dispatch, wish, toggleWish, notify } = useStore();
  const [showQuick, setShowQuick] = useState(false);
  const [hovered, setHovered] = useState(false);

  const disc = Math.round((1 - p.price / p.mrp) * 100);
  const wished = wish.includes(p.id);
  const meta = CAT_META[p.category] || { color:"#EC4899", light:"#FDF2F8", emoji:"👗" };
  const h = compact ? 210 : 270;

  const addCart = (e) => {
    e.stopPropagation();
    const hasSizeStock = p.sizeStock && Object.keys(p.sizeStock).length > 0;
    const firstInStockSize = hasSizeStock
      ? (p.sizes||[]).find(sz => (p.sizeStock[sz]||0) > 0) || (p.sizes||[])[0]
      : (p.sizes||[])[0];
    dispatch({ type:"ADD", p:{ ...p, sc:p.colors?.[0]||"Default", ss:firstInStockSize||"Free" } });
    notify(`${p.name} added!`);
    if (navigator.vibrate) navigator.vibrate(50);
  };

  return (
    <>
      {showQuick && <QuickView p={p} onClose={()=>setShowQuick(false)} onNav={onNav}/>}

      <div
        onClick={()=>onNav("product", p.id)}
        onMouseEnter={()=>setHovered(true)}
        onMouseLeave={()=>setHovered(false)}
        style={{
          background:"white", borderRadius:18, overflow:"hidden", cursor:"pointer",
          boxShadow: hovered ? `0 20px 44px ${meta.color}26, 0 4px 12px rgba(0,0,0,0.06)` : "0 2px 14px rgba(0,0,0,0.06)",
          border:"1px solid #f0f0f0",
          transition:"all 0.35s cubic-bezier(.2,.8,.2,1)",
          transform:hovered?"translateY(-6px)":"translateY(0)",
          position:"relative",
        }}>

        {/* Image */}
        <div style={{ height:h, background:p.img?"white":`linear-gradient(150deg,${meta.light},white)`, position:"relative", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
          {p.img
            ? <img src={p.img} alt={p.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top", transition:"transform 0.6s cubic-bezier(.2,.8,.2,1)", transform:hovered?"scale(1.09)":"scale(1)" }} loading="lazy"/>
            : <div style={{ textAlign:"center" }}>
                <div style={{ position:"absolute", inset:0, backgroundImage:`repeating-linear-gradient(45deg,${meta.color}08 0,transparent 1px,transparent 12px,${meta.color}08 13px)` }}/>
                <svg width={compact?50:70} height={compact?75:105} viewBox="0 0 60 90" fill="none" style={{ opacity:0.15, position:"relative" }}>
                  <ellipse cx="30" cy="10" rx="8" ry="8" fill={meta.color}/>
                  <path d="M18 22 Q30 18 42 22 L44 50 Q37 48 30 48 Q23 48 16 50 Z" fill={meta.color}/>
                  <path d="M16 50 L14 75 L20 75 L22 55 L38 55 L40 75 L46 75 L44 50 Z" fill={meta.color}/>
                </svg>
              </div>
          }

          {/* Soft vignette so badges/text stay legible over any photo */}
          <div style={{ position:"absolute", inset:0, background:"linear-gradient(to bottom, rgba(0,0,0,0.16) 0%, transparent 22%, transparent 100%)", pointerEvents:"none" }}/>

          {/* Signature fabric-tag ribbon (top-left corner fold) for badge text */}
          {p.badge && (
            <div style={{ position:"absolute", top:0, left:0, zIndex:2 }}>
              <div style={{
                background:`linear-gradient(135deg,${meta.color},${meta.color}cc)`,
                color:"white", fontSize:9, fontWeight:800, letterSpacing:0.5,
                padding:"6px 16px 6px 12px", borderRadius:"0 0 12px 0",
                boxShadow:`0 3px 10px ${meta.color}55`,
              }}>
                {p.badge}
              </div>
            </div>
          )}

          {disc > 0 && (
            <span style={{ position:"absolute", top:10, right:10, background:"#1a1a2e", color:"white", fontSize:9, fontWeight:800, padding:"4px 9px", borderRadius:8, letterSpacing:0.3, zIndex:2 }}>
              {disc}% OFF
            </span>
          )}

          {p.stock === 0 && (
            <div style={{ position:"absolute", inset:0, background:"rgba(255,255,255,0.85)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:2 }}>
              <span style={{ fontWeight:800, color:"#EF4444", fontSize:11, letterSpacing:2, border:"1.5px solid #EF4444", padding:"4px 14px", borderRadius:20 }}>OUT OF STOCK</span>
            </div>
          )}
          {p.stock > 0 && p.stock <= 5 && (
            <span style={{ position:"absolute", bottom:12, left:10, background:"rgba(245,158,11,0.95)", color:"white", fontSize:9, fontWeight:700, padding:"3px 10px", borderRadius:20, animation:"pulse 1.6s infinite", zIndex:2 }}>
              🔥 Only {p.stock} left
            </span>
          )}

          {/* Quick View — surfaces on hover */}
          {hovered && p.stock>0 && (
            <button onClick={e=>{e.stopPropagation();setShowQuick(true);if(navigator.vibrate)navigator.vibrate(30);}}
              style={{ position:"absolute", bottom:12, left:"50%", transform:"translateX(-50%)", background:"rgba(255,255,255,0.96)", border:"none", borderRadius:50, padding:"7px 18px", fontSize:11, fontWeight:700, cursor:"pointer", color:"#1a1a2e", boxShadow:"0 6px 20px rgba(0,0,0,0.18)", whiteSpace:"nowrap", animation:"fadeUp 0.2s ease", backdropFilter:"blur(8px)", zIndex:3 }}>
              👁️ Quick View
            </button>
          )}

          {/* Wishlist */}
          <button onClick={e=>{ e.stopPropagation(); toggleWish(p.id); if(navigator.vibrate)navigator.vibrate(30); }}
            style={{ position:"absolute", top:10, right:disc>0?46:10, background:"white", border:"none", borderRadius:"50%", width:28, height:28, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.12)", transition:"transform 0.2s", transform:wished?"scale(1.2)":"scale(1)", zIndex:2 }}>
            {wished ? "❤️" : "🤍"}
          </button>

          {/* Category-colored accent bar along the bottom edge of the photo */}
          <div style={{ position:"absolute", bottom:0, left:0, right:0, height:4, background:`linear-gradient(90deg,${meta.color},${meta.color}55)` }}/>
        </div>

        {/* Info */}
        <div style={{ padding: compact ? "11px 13px" : "15px 17px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:4 }}>
            <span style={{ width:5, height:5, borderRadius:"50%", background:meta.color, flexShrink:0 }}/>
            <span style={{ fontSize:9, color:meta.color, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>{p.category}</span>
          </div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:compact?13:15.5, fontWeight:700, color:"#1a1a2e", marginBottom:5, lineHeight:1.3 }}>{p.name}</div>
          <div style={{ fontSize:10, color:"#F59E0B", marginBottom:8 }}>
            {"★".repeat(Math.floor(p.rating||4.5))} <span style={{ color:"#999", fontSize:9 }}>({p.reviews})</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:6, flexWrap:"wrap" }}>
            <div>
              <span style={{ fontSize:compact?15.5:18, fontWeight:900, color:"#1a1a2e" }}>₹{p.price?.toLocaleString("en-IN")}</span>
              {p.mrp > p.price && <span style={{ fontSize:11, color:"#aaa", textDecoration:"line-through", marginLeft:6 }}>₹{p.mrp?.toLocaleString("en-IN")}</span>}
            </div>
            {p.stock > 0 && (
              <button onClick={addCart}
                style={{ background:`linear-gradient(135deg,${meta.color},${meta.color}cc)`, color:"white", border:"none", padding:"7px 15px", borderRadius:20, fontSize:10, fontWeight:800, cursor:"pointer", boxShadow:`0 4px 12px ${meta.color}40`, transition:"transform 0.2s" }}
                onMouseEnter={e=>{ e.currentTarget.style.transform="scale(1.06)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform="scale(1)"; }}>
                + Add
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
